// File: src/hooks/useContent/useContent.js

import { useState, useEffect, useRef, useCallback } from 'react';

// ↓ TOC helpers
import { loadOfflineToc, loadOnlineToc } from '../../services/tocApi';

// ↓ Article‑fetch helpers
import {
    fetchArticleOnline, fetchArticlesBatchOnline
} from '../../services/articleApi';

// ↓ Article‑handling (selection, transform, endless‑scroll …)
import { useArticleHandling } from './articleHandling';

import { Release } from '../../models/Release';

const useContent = ({ skipInitialSelect = false } = {}) => {
    const p = new URLSearchParams(window.location.search);

    const initialRelease = Release.fromUrl(p);
    const urlArticleId = p.get('articleId') || null;

    /* ------------------------------------------------------------------ */
    /*  State                                                             */
    /* ------------------------------------------------------------------ */
    const [tocData, setTocData] = useState([]);
    const [flatToc, setFlatToc] = useState([]);
    const [rawArticles, setRawArticles] = useState({});
    const [articles, setArticles] = useState({});
    const [currentArticleIndex, setCurrentArticleIndex] = useState(null);
    const [selectedArticleId, setSelectedArticleId] = useState(urlArticleId);
    const [selectedParentId, setSelectedParentId] = useState(null);
    const [selectedRelease, setSelectedReleaseState] = useState(initialRelease);
    const [loadingCount, setLoadingCount] = useState(0);
    const [error, setError] = useState(null);
    const [offlineMode, setOfflineMode] = useState(
        () => localStorage.getItem('offlineMode') === 'true'
    );

    /* ------------------------------------------------------------------ */
    /*  Helpers                                                           */
    /* ------------------------------------------------------------------ */
    const inc = useCallback(() => setLoadingCount(c => c + 1), []);
    const dec = useCallback(() => setLoadingCount(c => Math.max(0, c - 1)), []);
    const isLoading = loadingCount > 0;

    const previousReleaseRef = useRef(initialRelease);
    const contentAbortRef = useRef(null);
    const articleRequestsRef = useRef(new Set());
    const batchRequestsRef = useRef(new Map());

    const setSelectedRelease = useCallback((nextRelease) => {
        setSelectedReleaseState((current) => {
            const value = typeof nextRelease === 'function'
                ? nextRelease(current)
                : nextRelease;
            const release = Release.from(value);
            return current.equals(release) ? current : release;
        });
    }, []);

    /* Persist offline mode toggle */
    useEffect(() => {
        localStorage.setItem('offlineMode', offlineMode.toString());
    }, [offlineMode]);

    /* ------------------------------------------------------------------ */
    /*  (Re)Load TOC whenever language / version / mode changes           */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        setError(null);
        setTocData([]);
        setFlatToc([]);
        setRawArticles({});
        setArticles({});
        setCurrentArticleIndex(null);
        setSelectedArticleId(null);
        /* Keep selectedParentId – required for language mapping */

        // HeaderBar may need one render to resolve an incomplete/stale URL.
        // Never issue partial requests such as lang=null or product=null.
        if (!selectedRelease.isComplete) {
            contentAbortRef.current?.abort();
            contentAbortRef.current = null;
            return undefined;
        }

        const controller = new AbortController();
        contentAbortRef.current?.abort();
        contentAbortRef.current = controller;

        const pickInitial = (flat, previousRelease) => {
            if (skipInitialSelect) return null;

            const isInitialLoad = previousRelease.language === selectedRelease.language;
            if (
                isInitialLoad &&
                urlArticleId &&
                flat.some((i) => i._id === urlArticleId)
            ) {
                return urlArticleId;
            }

            if (previousRelease.language !== selectedRelease.language && selectedParentId) {
                const mapped = flat.find((i) => i.parent_id === selectedParentId);
                return mapped ? mapped._id : flat[0]?._id || null;
            }

            return flat[0]?._id || null;
        };

        const common = {
            selectedRelease,
            pickInitial,
            handleArticleSelect, // defined later via useArticleHandling
            setTocData,
            setFlatToc,
            inc,
            dec,
            setError,
            previousReleaseRef,
            signal: controller.signal,
        };

        offlineMode ? loadOfflineToc(common) : loadOnlineToc(common);
        return () => {
            controller.abort();
            if (contentAbortRef.current === controller) {
                contentAbortRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRelease, offlineMode]);

    /* ------------------------------------------------------------------ */
    /*  Wrap low‑level API helpers                                        */
    /* ------------------------------------------------------------------ */
    /**
     * Fetches an article by ID and provides it to the callback.
     * Attaches a signal to the fetch request.
     * @param id {string} - ID of the article to fetch
     * @param setArticle {function} - Callback to set the article content
     * @param is_topic {boolean | null} - Whether the article is a topic / glossary = true, else article page
     * If is_topic is null, the server will determine the type based on the article ID
    **/
    const fetchArticle = useCallback(async (id, setArticle, is_topic = null) => {
        if (articleRequestsRef.current.has(id)) {
            console.warn("Duplicate request for article", id);
            return;
        }
        articleRequestsRef.current.add(id);

        if (offlineMode) {
            // ToDo: Fetch article offline
        } else {
            await fetchArticleOnline({
                id,
                selectedRelease,
                is_topic,
                setArticle,
                setError,
                inc,
                dec,
                signal: contentAbortRef.current?.signal,
            });
        }

        articleRequestsRef.current.delete(id);
    }, [dec, inc, offlineMode, selectedRelease]);

    /**
     * Fetches a batch of articles by IDs and provides them to the callback.
     * Attaches a signal to the fetch request.
     * @param ids - Array of article IDs to fetch
     * @param setArticles - Callback to set the content of the articles
     */
    const fetchArticlesBatch = async (ids, setArticles) => {
        if (offlineMode) return; // ToDo: Fetch articles offline
        const key = ids.join(',');
        const signal = contentAbortRef.current?.signal;
        let request = batchRequestsRef.current.get(key);
        if (!request || request.signal !== signal) {
            request = {
                signal,
                promise: (async () => {
                    let documents = null;
                    await fetchArticlesBatchOnline({
                        ids,
                        selectedRelease,
                        setArticles: value => { documents = value; },
                        setError,
                        inc,
                        dec,
                        signal,
                    });
                    return documents;
                })(),
            };
            batchRequestsRef.current.set(key, request);
        }

        try {
            // Every consumer receives the shared response, including rapid navigation
            // back to an article whose earlier request is still in flight.
            const documents = await request.promise;
            if (documents !== null && !signal?.aborted) setArticles(documents);
        } finally {
            if (batchRequestsRef.current.get(key) === request) batchRequestsRef.current.delete(key);
        }
    };

    /* ------------------------------------------------------------------ */
    /*  High‑level article handling (selection, scrolling, in‑view, etc.) */
    /* ------------------------------------------------------------------ */
    const {
        handleArticleSelect,
        loadNextArticle,
        loadPrevArticle,
        handleArticleInView,
    } = useArticleHandling({
        flatToc,
        rawArticles,
        articles,
        setArticles,
        setRawArticles,
        selectedRelease,
        tocData,
        offlineMode,
        currentArticleIndex,
        setCurrentArticleIndex,
        setSelectedArticleId,
        setSelectedParentId,
        fetchArticle,
        fetchArticlesBatch,
        isLoading,
    });

    /* ------------------------------------------------------------------ */
    /*  Public API                                                        */
    /* ------------------------------------------------------------------ */
    return {
        tocData,
        flatToc,
        articles,
        currentArticleIndex,
        selectedArticleId,
        selectedRelease,
        setSelectedRelease,
        handleArticleSelect,
        loadNextArticle,
        loadPrevArticle,
        handleArticleInView,
        isLoading,
        error,
        offlineMode,
        setOfflineMode,
        fetchArticle,
        fetchArticlesBatch,
    };
};

export default useContent;
